import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { ClientsService } from '@intranet/modules/clients/clients.service';
import { LoginService } from '@intranet/modules/login/login.service';
import { UserService } from '@intranet/modules/users/user.service';
import { FinanceExtractQuery, FinanceSearchQuery } from '../../finance.types';
import {
    take,
    tap,
    takeUntil,
    debounceTime,
    distinctUntilChanged,
} from 'rxjs/operators';
import { FinanceService } from '../../finance.service';
import { FormGroup, NgForm } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { Client } from '@intranet/modules/clients/client.types';
import { User } from '@intranet/modules/users/user.types';
import { orderClientsByGroupName } from '@intranet/modules/clients/client.helpers';

@Component({
    selector: 'finance-listing-search',
    templateUrl: './finance-listing-search.component.html',
    styleUrls: ['./finance-listing-search.component.scss'],
})
export class FinanceListingSearchComponent implements OnInit {
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    @ViewChild('form', { static: true }) form: FormGroup;
    @Input() isMonthEnd: boolean = false;
    @Output() filter = new EventEmitter<FinanceSearchQuery>();
    @Output() extract = new EventEmitter<FinanceExtractQuery>();
    firstLoad: boolean = true;
    filterQuery: FinanceSearchQuery;
    clients: [Client[], Client[]] = [[], []];
    allClients: Client[] = [];
    activeUsers: User[] = [];
    otherUsers: User[] = [];
    groupedClients: any[];
    isFinance: boolean = false;

    // Phones: the filters other than Search are folded away behind a
    // "Filters" button (see the template and stylesheet) — open or not.
    filtersOpen = false;

    constructor(
        private _datePipe: DatePipe,
        private _loginService: LoginService,
        private _userService: UserService,
        private _clientService: ClientsService,
        private _financeService: FinanceService,
        private _route: ActivatedRoute
    ) {}

    ngOnInit(): void {
        // Check if user has finance permissions
        this.isFinance = this._loginService.checkAuth(['finance']);
        combineLatest([
            this._clientService.clients$,
            this._clientService.myClients$,
            this._userService.usersWithFinances$,
            this._financeService.filterQuery$,
        ])
            .pipe(
                take(1),
                tap(([allClients, myClients, usersWithFinances, filterQuery]) => {
                    // Clients
                    const [pinnedClients, restClients] =
                        orderClientsByGroupName(
                            allClients,
                            myClients.map((c) => c.clientId),
                            true
                        );

                    this.allClients = allClients;
                    this.clients = [
                        pinnedClients as Client[],
                        restClients as Client[],
                    ];

                    // Users
                    this.activeUsers = usersWithFinances.filter((u) => u.active);
                    this.otherUsers = usersWithFinances.filter((u) => !u.active);

                    // Month-end has its own "has defaulted" flag, since it
                    // needs to default to "All" independently of whichever
                    // tab (Estimates/Invoices) the user visited first in
                    // this /finance session
                    if (this.isMonthEnd) {
                        // If the URL carries filters (e.g. the user came
                        // back via the browser's back button from a linked
                        // job page), they take priority over any "default
                        // to current user" logic - the URL is the source of
                        // truth for "the filters as I left them"
                        const qp = this._route.snapshot.queryParams;
                        const urlKeys = ['clientId', 'startDate', 'endDate', 'userId', 'searchTerm'];
                        const hasUrlFilters = urlKeys.some((k) => qp[k] !== undefined);

                        if (hasUrlFilters) {
                            filterQuery = {
                                ...filterQuery,
                                // client-search's writeValue() only treats an
                                // actual `number` as a selection - URL query
                                // params are always strings, so this must be
                                // converted or it silently falls back to "All"
                                clientId: qp.clientId ? +qp.clientId : '',
                                startDate: qp.startDate ? new Date(qp.startDate) : null,
                                endDate: qp.endDate ? new Date(qp.endDate) : null,
                                userId: qp.userId ?? '',
                                searchTerm: qp.searchTerm ?? '',
                            };
                        } else if (!this._financeService.hasDefaultedMonthEndFilter) {
                            // Month-end is a cross-team view of all invoices,
                            // so it should default to "All" rather than the
                            // logged-in user
                            filterQuery.userId = '';
                        }

                        this._financeService.hasDefaultedMonthEndFilter = true;
                    } else if (!this._financeService.hasDefaultedFilters) {
                          const hasEntries = usersWithFinances.some(
                              u => u.id === this._loginService.userId
                          );

                          // if they actually have any finance docs, pre‐select them,
                          // otherwise leave blank == “All”
                          filterQuery.userId = hasEntries
                              ? this._loginService.userId
                              : '';

                          // don’t do this again until we leave /finance completely
                          this._financeService.hasDefaultedFilters = true;
                      }

                    // Set initial query values
                    this.filterQuery = { ...filterQuery };

                    if (!this.filterQuery.clientId) {
                        this.filterQuery.clientId = '';
                    }

                    // Fire on first load
                    this.filter.emit(this.filterQuery);
                })
            )
            .subscribe();

        this._financeService.filterQuery$
            .pipe(
                takeUntil(this._unsubscribeAll),
                tap((filterQuery) => {
                    this.filterQuery = { ...filterQuery };
                })
            )
            .subscribe();

        this.form.valueChanges
            .pipe(debounceTime(400), distinctUntilChanged())
            .subscribe((values) => {
                //console.log({ ...this.filterQuery, ...values });
                //if (!this.form.pristine) {
                this.filter.emit({ ...this.filterQuery, ...values });
                //}
            });
    }

    /** Phones, with the filters folded away: what they're set to, in a
     *  line — e.g. "Me · Any date" or "Acme · 1 Oct – 15 Oct · Anyone". */
    get filtersSummary(): string {
        const q = this.filterQuery;
        if (!q) return '';
        const parts: string[] = [];

        if (q.clientId) {
            const client = [...this.allClients, ...this.clients[0], ...this.clients[1]]
                .find((c) => String(c.clientId) === String(q.clientId));
            if (client) parts.push(client.displayName || client.clientName);
        }

        const day = (d: unknown) => (d ? this._datePipe.transform(d as Date, 'd MMM') : '');
        const from = day(q.startDate);
        const to = day(q.endDate);
        parts.push(from && to ? `${from} \u2013 ${to}` : from ? `From ${from}` : to ? `To ${to}` : 'Any date');

        const userId = q.userId || '';
        if (!userId) {
            parts.push('Anyone');
        } else if (userId === this._loginService.userId) {
            parts.push('Me');
        } else {
            const user = [...this.activeUsers, ...this.otherUsers].find((u) => u.id === userId);
            parts.push(user ? user.fullName || `${user.firstName} ${user.surname}` : '1 person');
        }

        return parts.join(' \u00b7 ');
    }

    onClearFilters() {
        this.form.setValue({
            clientId: '',
            userId: '',
            searchTerm: '',
            startDate: '',
            endDate: '',
        });
    }

    onClearDate(form: NgForm) {
        form.form.patchValue({
            startDate: '',
            endDate: '',
        });
    }

    onExtract() {
        const getTitle = (query: FinanceSearchQuery) => {
            let t = '';
            if (query.clientId) {
                t += this.allClients.find(
                    (c) => c.clientId == query.clientId
                )?.clientName;
            } else {
                t += 'All clients';
            }
            if (query.startDate) {
                t += `-${this._datePipe.transform(
                    query.startDate,
                    'yyyy-MM-dd'
                )}`;
            }
            if (query.endDate) {
                t += `-${this._datePipe.transform(
                    query.endDate,
                    'yyyy-MM-dd'
                )}`;
            }
            if (!query.startDate && !query.endDate) {
                t += '-Any date';
            }

            return t;
        };

        let extractQuery = { ...this.filterQuery } as FinanceExtractQuery;
        extractQuery.title = getTitle(this.filterQuery);
        this.extract.emit(extractQuery);
    }

    ngOnDestroy() {
        this._unsubscribeAll.next('');
        this._unsubscribeAll.complete();
    }
}
